/*
 * Subscription sticky cart bar.
 *
 * Each button picks the matching option in the Appstle widget first, then
 * hands off to the theme's own <product-form> submit, so the cart drawer,
 * loading states and error handling stay identical to a normal add to cart.
 *
 * Delegated on document, so it survives the section re-render the theme
 * performs on a variant change.
 *
 * The "scroll instead of add" path (products with variants) is left to
 * cart-bar.js via the data-cart-bar-scroll attribute — nothing here.
 */
(function () {
  'use strict';

  // Appstle writes its selling_plan input into the product form when the
  // option changes; give it a beat before we serialise the form.
  var SELECT_WAIT = 260;
  var RESET_AFTER = 600;

  function appstleOption(wantSubscription) {
    var wrappers = document.querySelectorAll('.appstle_subscription_wrapper_option');
    for (var i = 0; i < wrappers.length; i++) {
      var isSub = wrappers[i].classList.contains('payAsYouGoPlansDropdownWrapper');
      if (isSub === wantSubscription) { return wrappers[i]; }
    }
    return null;
  }

  function selectOption(wantSubscription) {
    var opt = appstleOption(wantSubscription);
    if (!opt) { return false; }
    var radio = opt.querySelector('input[name="selling_plan"]') || opt.querySelector('input[type="radio"]');
    if (!radio) { return false; }
    if (!radio.checked) { radio.click(); }
    return true;
  }

  function reset(btn) {
    btn.classList.remove('is-loading');
    btn.removeAttribute('disabled');
  }

  function submitVia(btn, event) {
    var bar = btn.closest('cart-bar');
    var info = bar && bar.closest('product-info');
    var form = info && info.querySelector('[data-product-form]');
    var productForm = form && form.closest('product-form');
    if (!productForm || typeof productForm.onSubmitHandler !== 'function') {
      reset(btn);
      return;
    }
    productForm.onSubmitHandler(event);
  }

  /*
   * Mirror the widget's current plan into the Subscribe button.
   *
   * The Liquid only knows the theme setting's flat discount, so without this
   * the bar always reads "30% Off · £29.39" even after the customer picks the
   * 2 month (25%) or 3 month (35%) plan. Both values are read straight from the
   * widget so the bar can never disagree with the card above it.
   */
  function subCard() {
    return document.querySelector('.payAsYouGoPlansDropdownWrapper');
  }

  function syncBarFromWidget() {
    var card = subCard();
    if (!card) { return; }

    // buildCustomPlans() hides the card while it cycles every plan to scrape
    // the save percentages. Those are intermediate values, not a selection.
    if (card.style.visibility === 'hidden') { return; }

    var bar = document.querySelector('.cart-bar--sub');
    if (!bar) { return; }

    var priceEl = bar.querySelector('[data-cbs-sub-price]');
    var amount = card.querySelector('.appstle_subscription_amount');
    if (priceEl && amount) {
      var price = amount.textContent.trim();
      if (price) { priceEl.textContent = price; }
    }

    var discountEl = bar.querySelector('[data-cbs-sub-discount]');
    if (discountEl) {
      // The card's own badge tracks the live selection; the per-plan badge on
      // the active card is the fallback for before Appstle has updated it.
      var badge = card.querySelector('.appstle-save-badge')
        || card.querySelector('.appstle-plan-box.is-active .appstle-plan-badge');
      var match = badge && badge.textContent.match(/(\d+)\s*%/);
      if (match) { discountEl.textContent = match[1]; }
    }
  }

  var syncPending = null;
  function syncSoon() {
    window.clearTimeout(syncPending);
    syncPending = window.setTimeout(function () {
      syncBarFromWidget();
      // The theme swaps the button out on a variant change, which silently
      // orphans the IntersectionObserver — re-point it at the new one.
      watchVisibility();
    }, 80);
  }

  // Plan clicks, the underlying select, and Appstle re-rendering the card.
  document.addEventListener('click', syncSoon, true);
  document.addEventListener('change', syncSoon, true);

  if (document.body) {
    new MutationObserver(syncSoon).observe(document.body, { childList: true, subtree: true });
  }

  syncBarFromWidget();
  watchVisibility();

  /*
   * Visibility: the bar shows whenever the main add to cart button is outside
   * the viewport, including at the very top of the page.
   *
   * cart-bar.js only reveals the bar once the whole buy form has scrolled
   * PAST (scrollY > wrapper top + wrapper height), so on first load — where
   * the button sits well below the fold on mobile — the bar stayed hidden
   * even though there was no way to add to cart.
   *
   * This uses its own class rather than touching .is-visible, so the theme's
   * scroll handler can keep toggling that without the two fighting: both
   * classes mean "show", and the theme only sets its one when the button is
   * already above the viewport, which is a subset of this rule.
   */
  var VISIBLE_CLASS = 'cbs-offscreen';
  var io = null;
  var observed = null;

  function mainAddToCart(bar) {
    // Scoped to the product section, and [data-add-to-cart] is only on the
    // theme's own button — the bar's buttons carry data-cbs-add.
    var info = bar.closest('product-info');
    return (info || document).querySelector('[data-product-form] [data-add-to-cart]');
  }

  function watchVisibility() {
    if (!('IntersectionObserver' in window)) { return; }

    var bar = document.querySelector('.cart-bar--sub');
    if (!bar) { return; }

    var btn = mainAddToCart(bar);

    // Button missing mid re-render — leave the bar as it is rather than
    // flashing it on and off.
    if (!btn || btn === observed) { return; }

    if (io) { io.disconnect(); }
    observed = btn;

    io = new IntersectionObserver(function (entries) {
      bar.classList.toggle(VISIBLE_CLASS, !entries[0].isIntersecting);
    }, { threshold: 0 });

    io.observe(btn);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-cbs-add]') : null;
    if (!btn || btn.hasAttribute('disabled')) { return; }

    e.preventDefault();

    var wantSubscription = btn.getAttribute('data-cbs-add') === 'subscription';
    var switched = selectOption(wantSubscription);

    // Never add a one-time item from the Subscribe button. If the widget
    // isn't there to switch, send the customer to the plan picker instead.
    if (wantSubscription && !switched) {
      var target = document.querySelector('.appstle_sub_widget')
        || document.querySelector('[data-product-form]');
      if (target) { target.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
      return;
    }

    btn.classList.add('is-loading');
    btn.setAttribute('disabled', 'disabled');

    window.setTimeout(function () {
      submitVia(btn, e);
      window.setTimeout(function () { reset(btn); }, RESET_AFTER);
    }, switched ? SELECT_WAIT : 0);
  });
})();
